import { nextTick } from 'vue'
import {
  sortedAutoRtRows,
  hasForceDisplayFormat,
  matchesRtRowCondition,
  matchesAutoRtExtraGate,
  resolveRequestableMode,
  isAutoRtRequestable,
  buildAutoRtDialogInfo
} from '@/plugins/rt-dialog-migration'
import { getAutoRtSession } from '@/composables/useAutoRtSession'

type RtCondition = {
  client_id: string
  permission_type: number
  request_able: boolean
  check_flag?: unknown
}

type UnknownRecord = Record<string, any>

const isShokumukeirekiFormat = (formatId: string): boolean => formatId.includes('shokumukeirekisho')

type DownloadItemVm = {
  $firebase: { user: { uid: string; email?: string } }
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $axios: { post: (url: string, data: FormData, config: { timeout: number }) => Promise<any> }
  $varuna: { endPoints: { displayDialog: string } }
  $globalVar: UnknownRecord
  isAgree?: boolean
  formatId?: string
}

function isSameAutoRtSelection (current: UnknownRecord, selected: UnknownRecord): boolean {
  return String(current.clientId || '') === String(selected.clientId || '') &&
    Number(current.permissionType || 0) === Number(selected.permissionType || 0) &&
    Number(current.oldPermissionType || 0) === Number(selected.oldPermissionType || 0)
}

export function useDownloadItemAutoRt (vm: DownloadItemVm) {
  const autoRt = getAutoRtSession()

  const notifyDisplay = (formatId: string, clientId: string, permissionType: number) => {
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatId)
    formData.append('dialog_name', 'auto_rt')
    formData.append('client_id', clientId)
    formData.append('permission_type', String(permissionType))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    formData.append('display_count_num', '1')
    vm.$axios.post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 }).catch((error: unknown) => {
      console.error('[useDownloadItemAutoRt] notifyDisplay failed', {
        formatId,
        clientId,
        permissionType,
        error
      })
    })
  }

  const selectAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord): UnknownRecord | null => {
    // ヤマト住建（is_BehindRT）向けには auto_rt を一切表示しない。
    if (vm.$globalVar.is_BehindRT && vm.$globalVar.is_BehindRT()) {
      return null
    }

    // shokumukeirekisho フォーマット時は keyword/grade/foreignStudent の判定対象を otherFormatData に切替える。
    const isShoku = isShokumukeirekiFormat(formatId)
    const conditionFormatData: UnknownRecord = isShoku ? otherFormatData : formatData
    const isMobileDevice = vm.$deviceLib.isMobileDevice === true

    const conditionCtx = {
      formatId,
      formatData: conditionFormatData,
      basicData,
      isMobileDevice,
      addressCheck: (bd: unknown, prefectures: string[]) => vm.$globalVar.addressCheck(bd, prefectures),
      keywordCheckRirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckRirekisho(fd, keywords),
      keywordCheckShokumukeirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckShokumukeirekisho(fd, keywords)
    }
    const gateCtx = {
      basicData,
      formatData: conditionFormatData,
      isMobileDevice,
      userEmail: (vm.$firebase && vm.$firebase.user && vm.$firebase.user.email) || ''
    }

    const rows = sortedAutoRtRows(rtconditions)
    for (const row of rows) {
      const clientId = String(row.client_id || '')
      const spec = resolveRequestableMode(row)
      // SetAutoRT:
      // - 旧 permission 行が API レスポンスに無くても表示可（行欠落 = 未制限）
      // - check_flag !== '1' なら他 permission 送客済みでも新が requestable なら表示可
      if (!isAutoRtRequestable(rtconditions, clientId, spec, row.check_flag)) continue

      const conditionMatched = hasForceDisplayFormat(row, formatId) ||
        (matchesRtRowCondition(row, conditionCtx) && matchesAutoRtExtraGate(row, gateCtx))
      if (!conditionMatched) continue

      return buildAutoRtDialogInfo(row)
    }
    return null
  }

  /**
   * 選定結果が現セッションと同等なら clear→set しない。
   * 選定なしのときだけ clear。変わったときだけ set。
   */
  const evaluateAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    const selected = selectAutoRT(rtconditions, basicData, formatId, formatData, otherFormatData)
    const current = autoRt.getAutoRtInfo()

    if (!selected) {
      if (current.clientId) {
        autoRt.clearAutoRtInfo()
      }
      return null
    }

    if (current.clientId && isSameAutoRtSelection(current, selected)) {
      return selected
    }

    autoRt.setAutoRtInfo({ ...selected, agreed: true })
    return selected
  }

  const handleAutoRt = (
    rtconditions: RtCondition[],
    basicData: UnknownRecord,
    formatId: string,
    formatData: UnknownRecord,
    otherFormatData: UnknownRecord
  ) => {
    evaluateAutoRT(rtconditions, basicData, formatId, formatData, otherFormatData)
    if (autoRt.getAutoRtInfo().clientId) {
      autoRt.setAgreed(!!vm.isAgree)
    }
  }

  const handleUpdateRtCondition = (
    clientId: string,
    permissionType: number | undefined | null,
    isSended: boolean,
    _dialogName: string
  ) => {
    // 未送信 close ではバナーを残す（auto_rt / 関連 RT1.0 共通）
    if (isSended !== true) {
      return
    }

    const info = autoRt.getAutoRtInfo()
    if (!info || !info.clientId) {
      return
    }

    if (String(info.clientId) !== String(clientId)) {
      return
    }

    const hasPermissionType = permissionType !== undefined && permissionType !== null
    const matchedPermission = !hasPermissionType ||
      Number(info.permissionType) === Number(permissionType) ||
      Number(info.oldPermissionType) === Number(permissionType)

    if (!matchedPermission) {
      return
    }

    // 関連 RT1.0（または同一 client の送客）成功 → auto_rt バナーを除外
    // FormatEdit 側のペア判定が同イベント内で autoRTInfo を参照するため nextTick で clear
    nextTick(() => {
      autoRt.clearAutoRtInfo()
    })
  }

  return {
    evaluateAutoRT,
    selectAutoRT,
    notifyDisplay,
    handleAutoRt,
    handleUpdateRtCondition,
    setAgreed: autoRt.setAgreed,
    clearAutoRtInfo: autoRt.clearAutoRtInfo,
    getAutoRtInfo: autoRt.getAutoRtInfo,
    isActive: () => !!autoRt.getAutoRtInfo().clientId
  }
}
